import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserRoleGuides = ({ userRole, onRoleChange }) => {
  const [expandedGuide, setExpandedGuide] = useState(null);

  // User roles configuration
  const userRoles = [
    { id: 'agent', label: 'Agents', icon: 'UserCheck', color: 'bg-blue-500' },
    { id: 'business', label: 'Apporteurs d\'affaires', icon: 'Briefcase', color: 'bg-green-500' },
    { id: 'venue', label: 'Venues', icon: 'MapPin', color: 'bg-purple-500' },
    { id: 'artist', label: 'Artistes', icon: 'Music', color: 'bg-orange-500' },
    { id: 'admin', label: 'Administrateurs', icon: 'Settings', color: 'bg-red-500' }
  ];

  // Role-specific guides and tutorials
  const roleGuides = {
    agent: {
      title: 'Guide pour Agents',
      description: 'Optimisez vos campagnes et maximisez vos commissions en tant qu\'agent Artis.io',
      sections: [
        {
          id: 'getting-started',
          title: 'Premiers Pas',
          icon: 'Play',
          items: [
            'Configuration de votre profil agent',
            'Comprendre le système de commissions',
            'Naviguer dans le tableau de bord',
            'Configurer vos préférences de notification'
          ]
        },
        {
          id: 'campaigns',
          title: 'Gestion des Campagnes',
          icon: 'Target',
          items: [
            'Créer une nouvelle campagne marketing',
            'Définir des objectifs et KPIs',
            'Segmenter votre audience cible',
            'Optimiser les performances de campagne'
          ]
        },
        {
          id: 'links',
          title: 'Génération de Liens',
          icon: 'Link',
          items: [
            'Créer des liens de suivi personnalisés',
            'Utiliser les templates de liens',
            'Implémenter les UTM parameters',
            'Analyser les clics et conversions'
          ]
        },
        {
          id: 'analytics',
          title: 'Analytics et Reporting',
          icon: 'BarChart',
          items: [
            'Interpréter les métriques de performance',
            'Créer des rapports personnalisés',
            'Exporter des données vers Excel',
            'Configurer des alertes automatiques'
          ]
        }
      ]
    },
    business: {
      title: 'Guide pour Apporteurs d\'Affaires',
      description: 'Développez votre réseau et maximisez vos opportunités business',
      sections: [
        {
          id: 'network',
          title: 'Développement Réseau',
          icon: 'Network',
          items: [
            'Identifier des prospects qualifiés',
            'Utiliser les outils de prospection',
            'Gérer votre pipeline commercial',
            'Suivre les opportunités business'
          ]
        },
        {
          id: 'partnerships',
          title: 'Gestion des Partenariats',
          icon: 'Handshake',
          items: [
            'Négocier les accords de partenariat',
            'Structurer les commissions multi-niveaux',
            'Monitorer la performance des partenaires',
            'Résoudre les conflits de partenariat'
          ]
        },
        {
          id: 'revenue',
          title: 'Optimisation Revenue',
          icon: 'DollarSign',
          items: [
            'Analyser la rentabilité par client',
            'Optimiser les marges commerciales',
            'Prévoir les revenus futurs',
            'Mettre en place des bonus de performance'
          ]
        }
      ]
    },
    venue: {
      title: 'Guide pour Venues',
      description: 'Gérez votre espace et optimisez vos événements musicaux',
      sections: [
        {
          id: 'space-management',
          title: 'Gestion d\'Espace',
          icon: 'Building',
          items: [
            'Configurer les espaces disponibles',
            'Gérer le calendrier des événements',
            'Optimiser la capacité d\'accueil',
            'Configurer les tarifs par période'
          ]
        },
        {
          id: 'bookings',
          title: 'Gestion des Réservations',
          icon: 'Calendar',
          items: [
            'Traiter les demandes de réservation',
            'Négocier les contrats d\'événement',
            'Gérer les annulations et reports',
            'Optimiser le taux d\'occupation'
          ]
        },
        {
          id: 'services',
          title: 'Services Additionnels',
          icon: 'Star',
          items: [
            'Proposer des services techniques',
            'Gérer la restauration et bar',
            'Coordonner la sécurité',
            'Optimiser l\'expérience client'
          ]
        }
      ]
    },
    artist: {
      title: 'Guide pour Artistes',
      description: 'Développez votre carrière et gérez vos performances sur Artis.io',
      sections: [
        {
          id: 'profile',
          title: 'Profil Artistique',
          icon: 'User',
          items: [
            'Créer un profil attractif',
            'Uploader votre portfolio musical',
            'Gérer votre agenda de disponibilité',
            'Optimiser votre visibilité'
          ]
        },
        {
          id: 'bookings',
          title: 'Gestion des Bookings',
          icon: 'Mic',
          items: [
            'Recevoir et négocier les offres',
            'Gérer vos contrats de performance',
            'Coordonner les aspects techniques',
            'Suivre vos revenus artistiques'
          ]
        },
        {
          id: 'promotion',
          title: 'Promotion et Marketing',
          icon: 'Megaphone',
          items: [
            'Utiliser les outils de promotion intégrés',
            'Collaborer avec des agents',
            'Analyser votre audience',
            'Développer votre fanbase'
          ]
        }
      ]
    },
    admin: {
      title: 'Guide pour Administrateurs',
      description: 'Gérez la plateforme et supervisez toutes les activités',
      sections: [
        {
          id: 'platform',
          title: 'Gestion Plateforme',
          icon: 'Settings',
          items: [
            'Configurer les paramètres système',
            'Gérer les utilisateurs et permissions',
            'Superviser les transactions',
            'Maintenir la sécurité des données'
          ]
        },
        {
          id: 'monitoring',
          title: 'Supervision et Contrôle',
          icon: 'Monitor',
          items: [
            'Surveiller les performances système',
            'Analyser les métriques business',
            'Gérer les incidents et alertes',
            'Optimiser les processus opérationnels'
          ]
        },
        {
          id: 'finance',
          title: 'Gestion Financière',
          icon: 'CreditCard',
          items: [
            'Superviser les flux de commissions',
            'Valider les paiements partenaires',
            'Analyser la rentabilité globale',
            'Gérer les litiges financiers'
          ]
        }
      ]
    }
  };

  const currentGuide = roleGuides?.[userRole];

  const toggleGuideExpansion = (sectionId) => {
    setExpandedGuide(expandedGuide === sectionId ? null : sectionId);
  };

  return (
    <div className="space-y-8">
      {/* Role Selector */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="text-xl font-semibold text-card-foreground mb-4">Choisissez votre rôle</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {userRoles?.map((role) => (
            <button
              key={role?.id}
              onClick={() => onRoleChange(role?.id)}
              className={`p-4 rounded-xl border-2 transition-all ${
                userRole === role?.id
                  ? 'border-accent bg-accent/10' :'border-border bg-card hover:border-accent/50'
              }`}
            >
              <div className={`w-10 h-10 ${role?.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                <Icon name={role?.icon} size={20} className="text-white" />
              </div>
              <div className="text-sm font-medium text-card-foreground">{role?.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Role Guide */}
      {currentGuide && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-card-foreground mb-2">{currentGuide?.title}</h2>
            <p className="text-muted-foreground">{currentGuide?.description}</p>
          </div>

          <div className="space-y-6">
            {currentGuide?.sections?.map((section) => (
              <div key={section?.id} className="border border-border rounded-lg">
                <button
                  onClick={() => toggleGuideExpansion(section?.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name={section?.icon} size={16} className="text-accent" />
                    </div>
                    <h3 className="text-lg font-semibold text-card-foreground">{section?.title}</h3>
                  </div>
                  <Icon 
                    name={expandedGuide === section?.id ? "ChevronUp" : "ChevronDown"} 
                    size={20} 
                    className="text-muted-foreground" 
                  />
                </button>

                {expandedGuide === section?.id && (
                  <div className="px-4 pb-4">
                    <div className="pl-11 space-y-3">
                      {section?.items?.map((item, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-muted-foreground">{item}</p>
                          </div>
                        </div>
                      ))}
                      
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex space-x-3">
                          <Button variant="default" size="sm">
                            <Icon name="PlayCircle" size={14} className="mr-2" />
                            Voir le Tutoriel
                          </Button>
                          <Button variant="outline" size="sm">
                            <Icon name="Download" size={14} className="mr-2" />
                            Guide PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Additional Resources */}
          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="BookOpen" size={20} className="text-accent" />
              <h3 className="text-lg font-semibold text-card-foreground">Ressources Supplémentaires</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <Icon name="Video" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tutoriels vidéo interactifs</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="FileText" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Guides PDF téléchargeables</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Support chat en direct</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Communauté d'utilisateurs</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoleGuides;